// src/components/navbar/banner/variants.tsx

"use client";

import Image from "next/image";
import Link from "next/link";
import { type ReactElement } from "react";

import type { BannerProps } from "./banner";

type VariantRenderer = (props: BannerProps) => ReactElement;

export const defaultBanner: VariantRenderer = ({
  title,
  description,
  href,
}) => (
  <Link href={href} className="block h-full w-full">
    <div className="h-full w-full rounded-[20px] bg-gradient-to-bl from-[#F9C828]/20 via-[#FF2F9C] to-[#F9C828]/20 p-[1px]">
      <div className="relative flex h-full w-full select-none items-center justify-between overflow-hidden rounded-[20px] bg-gradient-to-tl from-[#1C1729] via-[#1C1729] to-[#8B0E50] p-2 px-6 focus:shadow-md">
        <div className="flex flex-col gap-0.5">
          <p className="text-p1 font-semibold">{title}</p>
          <p className="text-p2 text-white/80">{description}</p>
        </div>
        <div className="flex items-center justify-center rounded-full bg-gradient-to-br from-[#FFB929] to-[#FF2F9C] p-[1px]">
          <div className="flex items-center justify-center rounded-full bg-[#010C15] p-4">
            <Image
              src="/svg/top-right-long-arrow.svg"
              width={16}
              height={16}
              alt="Zniżka dla firm"
            />
          </div>
        </div>
      </div>
    </div>
  </Link>
);

export const outlineBanner: VariantRenderer = ({
  title,
  description,
  href,
}) => (
  <Link href={href} className="block h-full w-full">
    <div className="h-full w-full rounded-[20px] bg-gradient-to-bl from-[#F9C828]/20 via-[#3824EB] to-[#F9C828]/20 p-[1px]">
      <div className="relative flex h-full w-full select-none items-center justify-between overflow-hidden rounded-[20px] bg-gradient-to-tl from-[#1C1729] via-[#1C1729] to-blue-900 p-2 px-6 focus:shadow-md">
        <div className="flex flex-col gap-4">
          <Image
            src="/svg/discount-vector.svg"
            width={20}
            height={20}
            alt="Zniżka dla studentów"
          />
          <div className="flex flex-col gap-2">
            <p className="text-p1 font-semibold">{title}</p>
            <p className="text-p2 text-muted-foreground">{description}</p>
          </div>
        </div>
      </div>
    </div>
  </Link>
);

export const imageBanner: VariantRenderer = ({
  title,
  description,
  href,
}) => (
  <Link href={href} className="block h-full">
    <div className="h-full w-full rounded-[20px] bg-gradient-to-bl from-[#F9C828]/20 via-[#FF2F9C] to-[#F9C828]/20 p-[1px]">
      <div className="relative flex h-full w-full select-none items-center justify-between overflow-hidden rounded-[20px] bg-gradient-to-tl from-[#1C1729] via-[#1C1729] to-[#8B0E50] p-2 px-6 focus:shadow-md">
        <div className="flex flex-col gap-4">
          <Image
            src="/svg/discount-vector.svg"
            width={20}
            height={20}
            alt="Zniżka dla studentów"
          />
          <div className="flex flex-col gap-2">
            <p className="text-p1 font-semibold">{title}</p>
            <p className="text-p2 text-muted-foreground">{description}</p>
          </div>
        </div>
      </div>
    </div>
  </Link>
);

export const companyAlternative: VariantRenderer = ({
  title,
  description,
  href,
}) => (
  <Link href={href} className="block h-full w-full">
    <div className="h-full w-full rounded-[20px] bg-gradient-to-bl from-[#F9C828]/20 via-[#FF2F9C] to-[#F9C828]/20 p-[1px]">
      <div className="relative flex h-full w-full select-none items-center justify-between overflow-hidden rounded-[20px] bg-gradient-to-tl from-[#1C1729] via-[#1C1729] to-[#8B0E50] p-2 px-6 focus:shadow-md">
        <div className="flex flex-col gap-4">
          <Image
            src="/svg/discount-vector.svg"
            width={20}
            height={20}
            alt="Zniżka dla studentów"
          />
          <div className="flex flex-col gap-2">
            <p className="text-p1 font-semibold">{title}</p>
            <p className="text-p2 text-muted-foreground">{description}</p>
          </div>
        </div>
      </div>
    </div>
  </Link>
);

export const variants: Record<
  NonNullable<BannerProps["variant"]>,
  VariantRenderer
> = {
  default: defaultBanner,
  "1": defaultBanner, // alias
  "2": outlineBanner,
  "3": imageBanner,
  "4": companyAlternative,
};